import { reactive, readonly } from "vue";
import notificationsApi from "./api";
import { BANNER_TTL_MS, MAX_VISIBLE_BANNERS, POLL_INTERVAL_MS } from "./constants";

// Одно состояние на всё приложение: колокольчик, баннеры и страница /notifications
// читают его, а не держат свои копии счётчика.
const state = reactive({
  unreadCount: 0,
  banners: [],
  // Растёт, когда пришли новые уведомления или прочитано всё — списки по нему перезагружаются.
  revision: 0,
});

// id последнего уведомления, которое пользователь уже «видел» как баннер или счётчик.
// null — ещё не знаем: первый опрос только ставит курсор, без баннеров.
let cursor = null;
let employeeId = null;
let pollTimer = null;
let started = false;
const bannerTimers = {};

function isVisible() {
  return document.visibilityState === "visible";
}

async function tick({ showBanners = true } = {}) {
  if (!isVisible()) return;
  let summary;
  try {
    summary = await notificationsApi.getSummary();
  } catch {
    return;
  }
  state.unreadCount = Number(summary.count) || 0;
  const latestId = Number(summary.latest_id) || 0;

  if (cursor === null || latestId < cursor) {
    cursor = latestId;
    return;
  }
  if (latestId === cursor) return;

  const since = cursor;
  cursor = latestId;
  state.revision += 1;
  if (!showBanners) return;

  try {
    // Самые свежие сверху; берём только пришедшие после курсора.
    const { items = [] } = await notificationsApi.getInbox({ page: 1, pageSize: MAX_VISIBLE_BANNERS });
    items
      .filter((row) => Number(row.notification_id) > since && !row.is_read)
      .reverse()
      .forEach(pushBanner);
  } catch {
    /* баннеры не критичны: уведомления всё равно есть в колокольчике */
  }
}

function onVisibilityChange() {
  // Пока вкладка была скрыта, опроса не было. Показываем то, что пришло за это время,
  // но не больше MAX_VISIBLE_BANNERS самых свежих — остальное видно по счётчику.
  if (isVisible()) tick();
}

function start() {
  if (started) return;
  started = true;
  tick();
  pollTimer = setInterval(tick, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

function stop() {
  started = false;
  clearInterval(pollTimer);
  pollTimer = null;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  Object.keys(bannerTimers).forEach(clearBannerTimer);
  state.banners = [];
}

// Смена пользователя без перезагрузки страницы: курсор и счётчик прежнего не годятся.
function setEmployee(id) {
  if (id == null || id === employeeId) return;
  const changed = employeeId !== null;
  employeeId = id;
  if (changed) reset();
}

function reset() {
  cursor = null;
  state.unreadCount = 0;
  state.banners = [];
  state.revision += 1;
  if (started) tick();
}

function clearBannerTimer(id) {
  clearTimeout(bannerTimers[id]);
  delete bannerTimers[id];
}

function scheduleBannerHide(id) {
  clearBannerTimer(id);
  bannerTimers[id] = setTimeout(() => dismissBanner(id), BANNER_TTL_MS);
}

function pushBanner(row) {
  if (state.banners.some((b) => b.notification_id === row.notification_id)) return;
  state.banners = [row, ...state.banners].slice(0, MAX_VISIBLE_BANNERS);
  scheduleBannerHide(row.notification_id);
}

function dismissBanner(id) {
  clearBannerTimer(id);
  state.banners = state.banners.filter((b) => b.notification_id !== id);
}

// Пока курсор над баннером — не прячем его.
function holdBanner(id) {
  clearBannerTimer(id);
}

function releaseBanner(id) {
  if (state.banners.some((b) => b.notification_id === id)) scheduleBannerHide(id);
}

// Отмечает прочитанным на сервере; свою копию строки вызывающий обновляет сам.
async function markRead(item) {
  if (!item || item.is_read) return false;
  try {
    await notificationsApi.markRead(item.notification_id);
    state.unreadCount = Math.max(0, state.unreadCount - 1);
    return true;
  } catch {
    return false;
  }
}

async function markAllRead() {
  await notificationsApi.markAllRead();
  state.unreadCount = 0;
  state.revision += 1;
}

// Клик по уведомлению: прочитать, убрать баннер, перейти по ссылке.
// Возвращает true, если уведомление только что стало прочитанным.
async function openNotification(item, router) {
  dismissBanner(item.notification_id);
  const marked = await markRead(item);
  if (item.link_url && router) {
    router.push(item.link_url).catch(() => {});
  }
  return marked;
}

export default function useNotifications() {
  return {
    state: readonly(state),
    start,
    stop,
    setEmployee,
    refresh: () => tick({ showBanners: false }),
    dismissBanner,
    holdBanner,
    releaseBanner,
    markRead,
    markAllRead,
    openNotification,
  };
}
